import { useQuery } from "@tanstack/react-query";
import {
  isAdminUser,
  queries,
  useAccountQuery,
  useAuthConfig,
} from "@theprelude/core";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  IconButton,
  MoreIcon,
} from "@theprelude/ds";
import { useState } from "react";
import { z } from "zod";
import { usePopper } from "@/hooks/use-popper";
import { getActivityQuery } from "@/lib/validators";
import { testOverlayQueryOptions } from "@/queries/tests";
import { DefaultOverlay } from "../default-overlay";
import { CopyTestModal } from "../modal/copy-test-modal";
import { DeleteTestModal } from "../modal/delete-test-modal";
import TestEdit from "./test-edit";
import TestView from "./test-view";

export const testSchema = z.tuple([
  z.literal("test"),
  z.object({
    id: z.string(),
  }),
]);

type TestSchema = z.infer<typeof testSchema>;

const TestOverlayContents = ({
  id,
  mode,
  setMode,
}: {
  id: string;
  mode: "edit" | "view";
  setMode: (mode: "edit" | "view") => void;
}) => {
  const authConfig = useAuthConfig();
  const query = getActivityQuery();

  const testOverlayQuery = testOverlayQueryOptions(
    authConfig,
    query,
    id,
  ).useSuspenseQuery();

  if (mode === "view") {
    return <TestView props={testOverlayQuery.data} />;
  }

  return <TestEdit props={testOverlayQuery.data} setMode={setMode} />;
};

const TestOverlay = ({ id }: TestSchema[1]) => {
  const [mode, setMode] = useState<"edit" | "view">("view");
  return (
    <DefaultOverlay
      title="Verified Security Test"
      actionsDropdown={<TestDropdown id={id} mode={mode} setMode={setMode} />}
    >
      <TestOverlayContents id={id} mode={mode} setMode={setMode} />
    </DefaultOverlay>
  );
};

function TestDropdown({
  id,
  mode,
  setMode,
}: {
  id: string;
  mode: "edit" | "view";
  setMode: (mode: "edit" | "view") => void;
}) {
  const deleteTestDialog = usePopper();
  const copyTestDialog = usePopper();
  const authConfig = useAuthConfig();
  const { data: account } = useAccountQuery();
  const { data: test } = useQuery(queries.detect.getTest(authConfig, id));
  const isAdmin = isAdminUser(account) ?? false;

  const cannotInteract =
    !test || test.account_id !== account?.account_id || !isAdmin;
  const isPreludeOwnedTest = test?.account_id === "prelude";

  if (isPreludeOwnedTest) {
    return (
      <>
        <Dropdown>
          <DropdownTrigger asChild>
            <IconButton className="size-4" icon={<MoreIcon />} />
          </DropdownTrigger>
          <DropdownContent align="end">
            <DropdownItem
              onSelect={() => setMode("edit")}
              disabled={!test || !isAdmin}
            >
              Edit
            </DropdownItem>
            {mode === "view" && (
              <DropdownItem onSelect={copyTestDialog.on}>
                Make a copy
              </DropdownItem>
            )}
          </DropdownContent>
        </Dropdown>
        <CopyTestModal {...copyTestDialog} id={id} />
      </>
    );
  }

  return (
    <>
      <Dropdown>
        <DropdownTrigger asChild>
          <IconButton className="size-4" icon={<MoreIcon />} />
        </DropdownTrigger>
        <DropdownContent align="end">
          <DropdownItem
            onSelect={() => setMode("edit")}
            disabled={!test || !isAdmin}
          >
            Edit
          </DropdownItem>
          {mode === "view" && (
            <DropdownItem onSelect={copyTestDialog.on}>
              Make a copy
            </DropdownItem>
          )}
          <DropdownItem
            onSelect={deleteTestDialog.on}
            disabled={cannotInteract}
          >
            Delete
          </DropdownItem>
        </DropdownContent>
      </Dropdown>

      <DeleteTestModal {...deleteTestDialog} id={id} />
      <CopyTestModal {...copyTestDialog} id={id} />
    </>
  );
}

export default TestOverlay;
